// src/legal/PrivacyPolicy.jsx
// The Privacy Policy text. Changing it means bumping LEGAL_VERSION (both copies — see
// legalConfig.js), which asks every user to accept again.
//
// EVERY CLAIM HERE MUST BE TRUE OF THE CODE. Two were deliberately worded against the
// system rather than the wish (30 Sep 2026):
//   - Visibility is per ORGANISATION, not per user. RLS policy tenant_isolation scopes
//     accounts, contacts, opportunities and the rest by tenant_id, so colleagues in the
//     same workspace see shared records. "No other user can see your data" is false.
//   - Encryption is TLS in transit and the database provider's disk encryption at rest.
//     There is no application-level encryption, and administrators can read stored data.
// If either changes, change the text in the same commit.
//
// Nothing here claims per-user privacy for any table. Some trees scope the personal
// assistant's tables per user (migration 0031) and some do not; the shared text states
// only what is true of all of them.

import React from 'react';
import Value from './Value';
import { ERASURE_DAYS, erasureMode } from './legalConfig';

const PROCESSORS = [
  ['Supabase',        'Database and sign-in',            'All stored data'],
  ['Vercel',          'Application hosting',             'Requests and technical logs'],
  ['Anthropic',       'AI assistant features',           'Your prompts and the records needed to answer them'],
  ['Brave Search',    'Web research',                    'Search terms only — never your records'],
  ['ZoomInfo',        'Company and contact enrichment',  'Names of companies or people you look up'],
  ['Deepgram',        'Meeting transcription',           'Only audio you submit for transcription'],
  ['Resend',          'Sending email',                   'Recipient address and message content'],
  ['Microsoft / Google', 'Sign-in, and mail and calendar', 'Mail and calendar only if you connect them'],
  ['Zoho',            'CRM synchronisation',             'Only if you connect Zoho'],
];

export default function PrivacyPolicy({ id, mode = erasureMode() }) {
  const onClose = mode === 'on-close';
  return (
    <>
      <section>
        <h2>1. Who we are</h2>
        <p>
          <Value v={id.product} /> (“the Service”) is operated by{' '}
          <Value v={id.operator} />. Privacy questions and requests:{' '}
          <Value v={id.contactEmail} mail />.
        </p>
      </section>

      <section>
        <h2>2. What we collect</h2>
        <ul>
          <li><strong>Account details</strong> — your name and email address, from the
            sign-up form or the Google or Microsoft account you sign in with. We do not
            see passwords for Google or Microsoft accounts.</li>
          <li><strong>Business data you enter or import</strong> — accounts, contacts,
            opportunities, leads, activities, meetings and notes.</li>
          <li><strong>AI conversations</strong> — what you ask the in-product assistants
            and what they reply, kept so sessions can be resumed and summarised.</li>
          <li><strong>Connected mailbox and calendar</strong> — only if you choose to
            connect Microsoft 365 or Google. Off by default, and you can disconnect at
            any time in Settings.</li>
          <li><strong>Meeting audio</strong> — only for meetings you choose to
            transcribe.</li>
          <li><strong>Technical logs</strong> — requests, errors and timings, used to run
            and fix the Service.</li>
        </ul>
      </section>

      <section className="lg-callout">
        <h2>3. Who can see your data</h2>
        <ul>
          <li><strong>No one outside your organisation.</strong> Every record belongs to
            one organisation (workspace), and the database itself enforces that boundary
            on every request — users of other organisations cannot see your data.</li>
          <li><strong>Members of your own organisation</strong> can see the records
            within it, such as accounts, contacts and opportunities.</li>
          <li><strong>System administrators</strong> of the Service can access all data,
            to operate, support and secure it.</li>
          <li><strong>The service providers below</strong> receive only what their
            function needs.</li>
        </ul>
      </section>

      <section className="lg-callout">
        <h2>4. How your data is protected</h2>
        <p>
          Data is encrypted in transit (HTTPS) and encrypted at rest by our database
          hosting provider. <strong>It is not encrypted at the application level</strong>:
          stored data is readable by system administrators and by the Service itself, which
          it needs in order to work. Access is controlled by sign-in and by the
          organisation boundary described above.
        </p>
      </section>

      <section>
        <h2>5. Service providers</h2>
        <div className="lg-table-wrap">
          <table>
            <thead><tr><th>Provider</th><th>Purpose</th><th>What it receives</th></tr></thead>
            <tbody>
              {PROCESSORS.map(([name, purpose, gets]) => (
                <tr key={name}><td>{name}</td><td>{purpose}</td><td>{gets}</td></tr>
              ))}
            </tbody>
          </table>
        </div>
        <p>
          The Service also reads public web pages and news feeds for research. That sends
          nothing about you or your records.
        </p>
      </section>

      <section>
        <h2>6. What we do not do</h2>
        <ul>
          <li>We do not sell your data or share it for advertising.</li>
          <li>We do not use your data to train AI models, and our AI provider does not
            train on it under its commercial terms.</li>
          <li>We do not read a mailbox or calendar you have not connected.</li>
        </ul>
      </section>

      <section className="lg-callout">
        <h2>7. Deleting your data{onClose ? ' when you leave' : ' — “forget me”'}</h2>
        {onClose ? (
          <p>
            The Service learns from what you do in it — that is how its recommendations
            become useful to you — so your data is kept for as long as your account is
            active. <strong>When you close your account or end your subscription</strong>,
            you can ask us to delete your data from{' '}
            <strong>Settings → Privacy → Close my account and delete my data</strong>, or
            by emailing <Value v={id.contactEmail} mail />. Your request is logged and
            tracked by a system administrator until it is closed, and you can see its
            status in Settings.
          </p>
        ) : (
          <p>
            You can ask us to delete your data at any time from{' '}
            <strong>Settings → Privacy → Request deletion of my data</strong>, or by
            emailing <Value v={id.contactEmail} mail />. Your request is logged and tracked
            by a system administrator until it is closed, and you can see its status in
            Settings.
          </p>
        )}
        <ul>
          <li><strong>We will complete it within {ERASURE_DAYS} days</strong>, including
            removal from backups and archives, and confirm by email.</li>
          <li><strong>We may keep data where the law requires it</strong> — for example
            for government, tax or regulatory purposes. Anything kept is retained only for
            that purpose and only as long as required, and we will tell you what was kept
            and why.</li>
          <li>Records shared within your organisation that other members rely on (for
            example a company account you created) may remain with the organisation, with
            your personal details removed.</li>
        </ul>
      </section>

      <section>
        <h2>8. Retention and your other rights</h2>
        <p>
          We keep your data while your account is active. You may ask for a copy of your
          data, for corrections, or for us to stop processing it (which means closing
          your account), by contacting <Value v={id.contactEmail} mail />.
        </p>
      </section>

      <section>
        <h2>9. Changes to this policy</h2>
        <p>
          When this policy changes, the date at the top changes and you will be asked to
          accept the new version before you continue using the Service.
        </p>
      </section>
    </>
  );
}
